import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { BarChart3, Plus, Users } from "lucide-react";
import { useId, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import {
	listPayrollRunsQueryOptions,
	useCreatePayrollRun,
} from "@/api/financial";
import { LoadingSpinner } from "@/components/LoadingSpinner";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
	FormRootMessage,
	useGuardedSubmit,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { NumberField } from "@/components/ui/number-field";
import { ResponsiveFormDialog } from "@/components/ui/responsive-form-dialog";
import { SubmitButton } from "@/components/ui/submit-button";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { useDateFormatter } from "@/hooks/useDateFormatter";
import {
	FinancialHeaderActions,
	HeaderActionButton,
} from "../-components/FinancialHeaderActions";
import { RunStatusBadge } from "./-components/PayrollBadges";
import { PayrollGate } from "./-components/PayrollGate";

export const Route = createFileRoute(
	"/_auth/app/_app/org/$orgId/financial/payroll/",
)({
	component: PayrollRunsPage,
});

function PayrollRunsPage() {
	const { orgId } = Route.useParams();
	return (
		<PayrollGate orgId={orgId}>
			<RunsContent orgId={orgId} />
		</PayrollGate>
	);
}

function RunsContent({ orgId }: { orgId: string }) {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { formatDateOnly, formatDateWithOptions } = useDateFormatter();
	const [isCreating, setIsCreating] = useState(false);

	const { data, isLoading } = useQuery(
		listPayrollRunsQueryOptions(orgId, { limit: "100" }),
	);
	const rows = data?.data ?? [];

	return (
		<div className="flex flex-col gap-4">
			<FinancialHeaderActions>
				<HeaderActionButton
					icon={Plus}
					label={t("financial.payroll.runs.new")}
					shortLabel={t("financial.payroll.runs.newShort")}
					onClick={() => setIsCreating(true)}
				/>
			</FinancialHeaderActions>

			<PageHeader title={t("financial.payroll.title")} showBackButton />

			<div className="grid gap-4 sm:grid-cols-2">
				<Card>
					<CardContent className="p-0">
						<Link
							to="/app/org/$orgId/financial/payroll/workers"
							params={{ orgId }}
							className="flex items-center justify-between gap-3 p-4 hover:bg-base-200/40"
						>
							<div className="flex items-center gap-3">
								<Users className="h-5 w-5 text-primary" />
								<p className="font-medium">
									{t("financial.payroll.workers.title")}
								</p>
							</div>
							<span className="text-sm font-medium text-primary">
								{t("financial.payroll.manage")}
							</span>
						</Link>
					</CardContent>
				</Card>
				<Card>
					<CardContent className="p-0">
						<Link
							to="/app/org/$orgId/financial/payroll/labor"
							params={{ orgId }}
							className="flex items-center justify-between gap-3 p-4 hover:bg-base-200/40"
						>
							<div className="flex items-center gap-3">
								<BarChart3 className="h-5 w-5 text-primary" />
								<p className="font-medium">
									{t("financial.payroll.labor.title")}
								</p>
							</div>
							<span className="text-sm font-medium text-primary">
								{t("financial.payroll.view")}
							</span>
						</Link>
					</CardContent>
				</Card>
			</div>

			<div className="card bg-base-100 shadow-md">
				<div className="card-body">
					{isLoading ? (
						<div className="flex justify-center py-16">
							<LoadingSpinner />
						</div>
					) : rows.length === 0 ? (
						<div className="py-12 text-center">
							<p className="font-medium">
								{t("financial.payroll.runs.empty.title")}
							</p>
							<p className="mt-1 text-sm text-base-content/60">
								{t("financial.payroll.runs.empty.description")}
							</p>
						</div>
					) : (
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead>
										{t("financial.payroll.runs.columns.competency")}
									</TableHead>
									<TableHead className="hidden sm:table-cell">
										{t("financial.payroll.runs.columns.label")}
									</TableHead>
									<TableHead>
										{t("financial.payroll.runs.columns.paymentDate")}
									</TableHead>
									<TableHead>
										{t("financial.payroll.runs.columns.status")}
									</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{rows.map((run) => (
									<TableRow
										key={run.id}
										className="cursor-pointer"
										onClick={() =>
											navigate({
												to: "/app/org/$orgId/financial/payroll/runs/$runId",
												params: { orgId, runId: run.id },
											})
										}
									>
										<TableCell className="font-medium">
											{formatDateWithOptions(
												new Date(run.competencyMonth).getTime(),
												{ month: "long", year: "numeric", timeZone: "UTC" },
											)}
										</TableCell>
										<TableCell className="hidden sm:table-cell text-base-content/70">
											{run.label ?? "—"}
										</TableCell>
										<TableCell>
											{formatDateOnly(new Date(run.paymentDate).getTime())}
										</TableCell>
										<TableCell>
											<RunStatusBadge status={run.status} />
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					)}
				</div>
			</div>

			{isCreating && (
				<NewRunDialog
					orgId={orgId}
					onClose={() => setIsCreating(false)}
					onCreated={(runId) =>
						navigate({
							to: "/app/org/$orgId/financial/payroll/runs/$runId",
							params: { orgId, runId },
						})
					}
				/>
			)}
		</div>
	);
}

function currentMonthValue(): string {
	const now = new Date();
	return `${now.getFullYear()}-${`${now.getMonth() + 1}`.padStart(2, "0")}`;
}

function todayInputValue(): string {
	const now = new Date();
	const month = `${now.getMonth() + 1}`.padStart(2, "0");
	const day = `${now.getDate()}`.padStart(2, "0");
	return `${now.getFullYear()}-${month}-${day}`;
}

const newRunSchema = z.object({
	competencyMonth: z
		.string()
		.min(1, "forms.validation.required")
		.regex(/^\d{4}-\d{2}$/, "forms.validation.invalidDate"),
	paymentDate: z
		.string()
		.min(1, "forms.validation.required")
		.regex(/^\d{4}-\d{2}-\d{2}$/, "forms.validation.invalidDate"),
	label: z.string(),
	defaultDaysWorked: z
		.string()
		.regex(/^\d*$/, "forms.validation.invalidNumber")
		.refine(
			(value) => value === "" || Number(value) <= 31,
			"forms.validation.daysWorkedRange",
		),
});
type NewRunFormValues = z.infer<typeof newRunSchema>;

function NewRunDialog({
	orgId,
	onClose,
	onCreated,
}: {
	orgId: string;
	onClose: () => void;
	onCreated: (runId: string) => void;
}) {
	const { t } = useTranslation();
	const formId = useId();
	const createMutation = useCreatePayrollRun();

	const form = useForm<NewRunFormValues>({
		resolver: zodResolver(newRunSchema),
		mode: "onTouched",
		reValidateMode: "onChange",
		defaultValues: {
			competencyMonth: currentMonthValue(),
			paymentDate: todayInputValue(),
			label: "",
			defaultDaysWorked: "",
		},
	});

	const guardedSubmit = useGuardedSubmit(form, (values) =>
		createMutation
			.mutateAsync({
				organizationId: orgId,
				data: {
					competencyMonth: new Date(
						`${values.competencyMonth}-01T12:00:00.000Z`,
					).toISOString(),
					paymentDate: new Date(values.paymentDate).toISOString(),
					label: values.label.trim() || null,
					...(values.defaultDaysWorked !== ""
						? { defaultDaysWorked: Number(values.defaultDaysWorked) }
						: {}),
				},
			})
			.then((run) => onCreated(run.id)),
	);

	return (
		<ResponsiveFormDialog
			open
			onOpenChange={(open) => !open && onClose()}
			title={t("financial.payroll.runs.newTitle")}
			pending={createMutation.isPending}
			footer={
				<>
					<Button
						type="button"
						variant="outline"
						disabled={createMutation.isPending}
						onClick={onClose}
					>
						{t("common.cancel")}
					</Button>
					<SubmitButton form={formId} pending={createMutation.isPending}>
						{t("financial.payroll.runs.new")}
					</SubmitButton>
				</>
			}
		>
			<Form {...form}>
				<form id={formId} className="space-y-4 py-2" onSubmit={guardedSubmit}>
					<FormField
						control={form.control}
						name="competencyMonth"
						render={({ field }) => (
							<FormItem>
								<FormLabel required>
									{t("financial.payroll.runs.form.competency")}
								</FormLabel>
								<FormControl>
									<Input type="month" {...field} />
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
					<FormField
						control={form.control}
						name="paymentDate"
						render={({ field }) => (
							<FormItem>
								<FormLabel required>
									{t("financial.payroll.runs.form.paymentDate")}
								</FormLabel>
								<FormControl>
									<Input type="date" {...field} />
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
					<FormField
						control={form.control}
						name="label"
						render={({ field }) => (
							<FormItem>
								<FormLabel>{t("financial.payroll.runs.form.label")}</FormLabel>
								<FormControl>
									<Input
										{...field}
										placeholder={t(
											"financial.payroll.runs.form.labelPlaceholder",
										)}
									/>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
					<FormField
						control={form.control}
						name="defaultDaysWorked"
						render={({ field }) => (
							<FormItem>
								<FormLabel>
									{t("financial.payroll.runs.form.defaultDaysWorked")}
								</FormLabel>
								<FormControl>
									<NumberField
										mode="numeric"
										value={field.value}
										onChange={field.onChange}
										onBlur={field.onBlur}
										name={field.name}
										ref={field.ref}
									/>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
					<FormRootMessage />
				</form>
			</Form>
		</ResponsiveFormDialog>
	);
}
